import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useLang } from '../LangContext';
import { statusText as localizedStatus } from '../i18n';
import { AccessDenied, canAccess } from '../adminAccess';
import DatabaseError from '../components/DatabaseError';
import { reviewErrorMessage } from '../orderServiceUtils';
import { statusLabel } from '../orderAdminUtils';
import {
  OrderActivityPanel, OrderClientReportPanel, OrderFactsPanel,
  OrderProofPanel,
} from './OrdersPage';

const TABS = [
  { key: 'submitted', label: 'Submitted', tone: 'amber' },
  { key: 'under_review', label: 'Under Review', tone: 'blue' },
  { key: 'refund_approved', label: 'Refund Approved', tone: 'green' },
  { key: 'refund_rejected', label: 'Refund Rejected', tone: 'red' },
  { key: 'resolved_without_refund', label: 'Resolved', tone: 'gray' },
  { key: 'all', label: 'All', tone: 'gray' },
];

const TONE = {
  amber: 'bg-amber-100 text-amber-700',
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  red: 'bg-red-100 text-red-700',
  gray: 'bg-gray-100 text-gray-600',
};

const COMPLAINT_STATUS_TONE = {
  submitted: 'amber',
  under_review: 'blue',
  refund_approved: 'green',
  refund_rejected: 'red',
  resolved_without_refund: 'gray',
  closed: 'gray',
};

const COMPLAINT_STATUS_LABEL = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  refund_approved: 'Refund Approved',
  refund_rejected: 'Refund Rejected',
  resolved_without_refund: 'Resolved (no refund)',
  closed: 'Closed',
};

const CATEGORY_LABEL = {
  service_quality: 'Service quality',
  work_incomplete: 'Work incomplete',
  damage_concern: 'Damage concern',
  incorrect_service: 'Incorrect service',
  worker_conduct: 'Worker conduct',
  report_issue: 'Photo/video report issue',
  other: 'Other',
};

const stamp = value => {
  if (!value) return '—';
  if (typeof value === 'string') return new Date(value).toLocaleString();
  if (value.toDate) return value.toDate().toLocaleString();
  return '—';
};

/**
 * COMPLAINTS — customer cases that may end in a refund.
 *
 * A complaint is not a support message. It carries a category, evidence and an
 * admin decision, and `approve_refund` moves real money through Payriff, so
 * every decision here goes through the `reviewComplaint` callable rather than
 * a direct Firestore write.
 */
export default function ComplaintsPage({ user }) {
  const { tr, lang } = useLang();
  const [complaints, setComplaints] = useState([]);
  const [tab, setTab] = useState('submitted');
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    // A single in-flight refresh at a time: a second concurrent read would
    // race the first and could render the older result last.
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'complaints'), orderBy('createdAt', 'desc')),
      );
      setComplaints(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const map = { all: complaints.length };
    for (const t of TABS) {
      if (t.key === 'all') continue;
      map[t.key] = complaints.filter(c => c.status === t.key).length;
    }
    return map;
  }, [complaints]);

  const rows = tab === 'all'
    ? complaints
    : complaints.filter(c => c.status === tab);

  const current = selectedId
    ? complaints.find(c => c.id === selectedId) || null
    : null;

  if (!canAccess(user, 'orders')) return <AccessDenied title="Complaints" />;

  return (
    <div className="page-shell">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="page-title">{tr('complaints')}</h1>
          <p className="page-description">{tr('complaintsDescription')}</p>
        </div>
        <button className="btn-secondary" onClick={load} disabled={loading}>
          {loading ? `${tr('refreshing')}…` : tr('refresh')}
        </button>
      </div>

      {error && <DatabaseError message={error} />}

      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
              tab === t.key ? 'bg-primary text-white' : `${TONE[t.tone]} hover:opacity-80`
            }`}>
            {t.label}{counts[t.key] ? ` (${counts[t.key]})` : ''}
          </button>
        ))}
      </div>

      {rows.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <p className="text-gray-400 text-sm">{tr('nothingInQueue')}</p>
        </div>
      )}

      <div className="grid gap-3">
        {rows.map(c => (
          <div key={c.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-xs text-gray-400">{c.orderNumber || c.orderId}</p>
                <p className="font-semibold text-gray-900">
                  {c.customerName || c.customerEmail || c.customerId || '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {CATEGORY_LABEL[c.category] || c.category} · {c.packageName || '—'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Submitted {stamp(c.createdAt)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`badge ${TONE[COMPLAINT_STATUS_TONE[c.status] || 'gray']}`}>
                  {localizedStatus(lang, c.status)}
                </span>
                <span className={`badge ${
                  String(c.paymentStatus || '').toLowerCase() === 'paid'
                    ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {localizedStatus(lang, c.paymentStatus || 'unpaid')}
                  {c.orderAmount ? ` · ${c.orderAmount} AZN` : ''}
                </span>
                <button
                  onClick={() => setSelectedId(c.id)}
                  className="btn-primary text-xs px-3 py-1.5">
                  {tr('review')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {current && (
        <ComplaintModal
          complaint={current}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

/** The full case: the complaint, the order's own evidence, and the decision. */
function ComplaintModal({ complaint, onClose, onChanged }) {
  const { lang } = useLang();
  const [order, setOrder] = useState(null);
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!complaint.orderId) return;
      const snap = await getDoc(doc(db, 'orders', complaint.orderId));
      if (!cancelled && snap.exists()) {
        setOrder({ id: snap.id, ...snap.data() });
      }
    })();
    return () => { cancelled = true; };
  }, [complaint.orderId]);

  const decided = ['refund_approved', 'refund_rejected',
    'resolved_without_refund', 'closed'].includes(complaint.status);

  const refundable = Math.max(
    0,
    Math.round((Number(complaint.orderAmount || 0) - Number(complaint.refundedAmount || 0)) * 100) / 100,
  );

  async function decide(action) {
    setError('');
    setDone('');
    setBusy(action);
    try {
      const call = httpsCallable(functions, 'reviewComplaint');
      const payload = { complaintId: complaint.id, action, note: note.trim() };
      if (action === 'approve_refund') {
        payload.amount = Number(amount || refundable);
      }
      const result = await call(payload);
      setDone(result?.data?.status || 'done');
      setNote('');
      await onChanged();
    } catch (e) {
      setError(reviewErrorMessage(e));
    } finally {
      setBusy('');
    }
  }

  const history = Array.isArray(complaint.history) ? complaint.history : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">
            Complaint · {complaint.orderNumber || complaint.orderId}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="p-6 space-y-1">
          <div className="text-sm text-gray-600">
            <p><span className="text-gray-400">Customer:</span> {complaint.customerName || complaint.customerEmail || '—'}</p>
            <p><span className="text-gray-400">Order status:</span> {order ? statusLabel(order.status, lang) : '—'}</p>
            <p><span className="text-gray-400">Submitted:</span> {stamp(complaint.createdAt)}</p>
          </div>

          <div className="pt-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">CUSTOMER COMPLAINT</p>
            <div className="rounded-xl border border-gray-100 p-3">
              <span className="badge bg-purple-100 text-purple-700">
                {CATEGORY_LABEL[complaint.category] || complaint.category}
              </span>
              <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap">
                {complaint.description}
              </p>
            </div>
          </div>

          {Array.isArray(complaint.evidenceUrls) && complaint.evidenceUrls.length > 0 && (
            <div className="pt-4">
              <p className="text-xs font-semibold text-gray-500 mb-2">CUSTOMER EVIDENCE</p>
              <div className="grid grid-cols-3 gap-2">
                {complaint.evidenceUrls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt="evidence"
                         className="w-full h-24 object-cover rounded-xl bg-gray-100" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {order && (
            <>
              <OrderFactsPanel order={order} />
              {/* The same two separate media sections the review queue shows:
                  what the customer received, and what the worker submitted. */}
              <OrderClientReportPanel order={order} />
              <OrderProofPanel order={order} label="submitted for approval" />
              <OrderActivityPanel order={order} />
            </>
          )}

          <div className="pt-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">PAYMENT</p>
            <div className="rounded-xl border border-gray-100 px-3 py-2 text-sm text-gray-700">
              <p>Paid: {localizedStatus(lang, complaint.paymentStatus || 'unpaid')} · {complaint.orderAmount || 0} AZN</p>
              <p>Already refunded: {complaint.refundedAmount || 0} AZN</p>
              <p className="font-semibold">Refundable now: {refundable} AZN</p>
              {complaint.refundAmount != null && (
                <p className="text-green-700 font-semibold mt-1">
                  Refunded {complaint.refundAmount} AZN
                  {complaint.refundProviderStatus ? ` · provider: ${complaint.refundProviderStatus}` : ''}
                </p>
              )}
            </div>
          </div>

          <div className="pt-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">ADMIN DECISION</p>

            {decided ? (
              <div className="rounded-xl border border-gray-100 px-3 py-2">
                <span className={`badge ${TONE[COMPLAINT_STATUS_TONE[complaint.status] || 'gray']}`}>
                  {localizedStatus(lang, complaint.status)}
                </span>
                {complaint.decisionNote && (
                  <p className="text-sm text-gray-700 mt-2">{complaint.decisionNote}</p>
                )}
                <p className="text-[11px] text-gray-400 mt-1">
                  {complaint.reviewedByEmail || ''} {stamp(complaint.reviewedAt)}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <textarea
                  className="input-field w-full"
                  rows={3}
                  placeholder="Decision note — the customer is shown this text exactly as written."
                  value={note}
                  onChange={e => setNote(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  {complaint.status === 'submitted' && (
                    <button className="btn-secondary" disabled={Boolean(busy)}
                            onClick={() => decide('start_review')}>
                      {busy === 'start_review' ? '...' : 'Start Review'}
                    </button>
                  )}
                  <button className="btn-secondary" disabled={Boolean(busy)}
                          onClick={() => decide('resolve_without_refund')}>
                    {busy === 'resolve_without_refund' ? '...' : 'Resolve without Refund'}
                  </button>
                  <button className="btn-danger" disabled={Boolean(busy)}
                          onClick={() => decide('reject_refund')}>
                    {busy === 'reject_refund' ? '...' : 'Reject Refund'}
                  </button>
                </div>

                <div className="rounded-xl border border-green-200 bg-green-50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-green-800">
                    APPROVE REFUND
                  </p>
                  <p className="text-[11px] text-green-700">
                    The refund is sent to Payriff and verified before the
                    complaint is marked approved. If Payriff refuses, nothing
                    is marked refunded and the error is shown here.
                  </p>
                  <input
                    className="input-field w-full"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={`Amount in AZN (max ${refundable})`}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                  />
                  <button
                    className="btn-primary w-full"
                    disabled={Boolean(busy) || refundable <= 0}
                    title={refundable <= 0 ? 'Nothing left to refund on this order.' : ''}
                    onClick={() => decide('approve_refund')}>
                    {busy === 'approve_refund' ? '...' : 'Approve Refund'}
                  </button>
                </div>
              </div>
            )}

            {error && <p className="text-red-600 text-xs mt-2">{error}</p>}
            {done && (
              <p className="text-green-600 text-xs mt-2">
                Complaint is now {COMPLAINT_STATUS_LABEL[done] || done}.
              </p>
            )}
          </div>

          {history.length > 0 && (
            <div className="pt-4">
              <p className="text-xs font-semibold text-gray-500 mb-2">CASE HISTORY</p>
              <ol className="rounded-xl border border-gray-100 divide-y divide-gray-50">
                {history.map((entry, i) => (
                  <li key={i} className="px-3 py-2 text-xs">
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-semibold text-gray-700">{entry.event}</span>
                      <span className="text-gray-400 whitespace-nowrap">{stamp(entry.createdAt)}</span>
                    </div>
                    <p className="text-gray-500">
                      {entry.actorRole || 'unknown'}
                      {entry.amount != null ? ` · ${entry.amount} AZN` : ''}
                    </p>
                    {entry.note && <p className="text-gray-600 mt-0.5">{entry.note}</p>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
